import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { AnnotationLocale, TypeAnnotation } from '../lib/types'

/**
 * Annotations des plans : la couche dessinée par-dessus la page, la barre
 * d'outils et la fenêtre de saisie du texte.
 *
 * Coordonnées : tout est enregistré de 0 à 1 (x sur la largeur de la page,
 * y sur sa hauteur). À l'affichage, la page mesure 1000 unités de large et
 * 1000 × (hauteur / largeur) de haut ; l'épaisseur est une fraction de la
 * largeur. Une annotation grossit donc avec le zoom, comme de l'encre sur
 * le papier.
 */

export type Outil = 'main' | 'choisir' | TypeAnnotation
type Point = [number, number]

export const COULEURS = [
  { valeur: '#E02424', nom: 'Rouge' },
  { valeur: '#F28C00', nom: 'Orange' },
  { valeur: '#FFCA00', nom: 'Jaune' },
  { valeur: '#1E9E4A', nom: 'Vert' },
  { valeur: '#2563EB', nom: 'Bleu' },
  { valeur: '#000000', nom: 'Noir' }
]

/** Trois épaisseurs, en fraction de la largeur de la page. */
export const EPAISSEURS = [
  { valeur: 0.0012, nom: 'Fin' },
  { valeur: 0.0025, nom: 'Moyen' },
  { valeur: 0.005, nom: 'Épais' }
]

const L = 1000 // largeur de la page en unités d'affichage
const taillePolice = (e: number) => e * L * 8
const traitSurligneur = (e: number) => e * L * 7

/** Distance d'un point à un segment, en unités normalisées corrigées du format de la page. */
function distSegment(p: Point, a: Point, b: Point, ratio: number): number {
  const px = p[0], py = p[1] * ratio
  const ax = a[0], ay = a[1] * ratio
  const bx = b[0], by = b[1] * ratio
  const dx = bx - ax, dy = by - ay
  const l2 = dx * dx + dy * dy
  const t = l2 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / l2)) : 0
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
}

function pointsEllipse(a: Point, b: Point): Point[] {
  const cx = (a[0] + b[0]) / 2, cy = (a[1] + b[1]) / 2
  const rx = Math.abs(b[0] - a[0]) / 2, ry = Math.abs(b[1] - a[1]) / 2
  return Array.from({ length: 41 }, (_, i) => {
    const t = (i / 40) * Math.PI * 2
    return [cx + rx * Math.cos(t), cy + ry * Math.sin(t)] as Point
  })
}

/** Boîte englobante (normalisée) d'un texte, approximative. */
function boiteTexte(a: AnnotationLocale, ratio: number) {
  const fs = taillePolice(a.epaisseur) / L // en fraction de la largeur
  const lignes = (a.texte ?? '').split('\n')
  const larg = Math.max(...lignes.map((l) => l.length)) * fs * 0.58
  const haut = (lignes.length * fs * 1.25) / ratio
  const [x, y] = a.geometrie.p[0]
  return { x0: x, y0: y - (fs * 0.95) / ratio, x1: x + larg, y1: y - (fs * 0.95) / ratio + haut }
}

/** Distance entre un point et une annotation (0 si le point est dessus). */
function distance(p: Point, a: AnnotationLocale, ratio: number): number {
  const pts = a.geometrie.p
  if (a.type === 'texte') {
    const b = boiteTexte(a, ratio)
    const dx = Math.max(b.x0 - p[0], 0, p[0] - b.x1)
    const dy = Math.max(b.y0 - p[1], 0, p[1] - b.y1) * ratio
    return Math.hypot(dx, dy)
  }
  const chemin = a.type === 'cercle' ? pointsEllipse(pts[0], pts[1]) : pts
  let d = Infinity
  for (let i = 0; i < chemin.length - 1; i++) d = Math.min(d, distSegment(p, chemin[i], chemin[i + 1], ratio))
  if (chemin.length === 1) d = distSegment(p, chemin[0], chemin[0], ratio)
  const demiTrait = (a.type === 'surligneur' ? traitSurligneur(a.epaisseur) : a.epaisseur * L) / L / 2
  return Math.max(0, d - demiTrait)
}

/** Dessin SVG d'une annotation. */
function Forme({ a, ratio, choisie }: { a: Pick<AnnotationLocale, 'type' | 'couleur' | 'epaisseur' | 'geometrie' | 'texte'>; ratio: number; choisie?: boolean }) {
  const H = L * ratio
  const pts = a.geometrie.p.map(([x, y]) => [x * L, y * H] as Point)
  const w = a.epaisseur * L
  const commun = { stroke: a.couleur, strokeWidth: w, fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  let forme: ReactNode = null

  if (a.type === 'libre' || a.type === 'surligneur') {
    const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ')
    forme =
      a.type === 'surligneur' ? (
        <path d={d} {...commun} strokeWidth={traitSurligneur(a.epaisseur)} strokeOpacity={0.38} strokeLinecap="butt" style={{ mixBlendMode: 'multiply' }} />
      ) : (
        <path d={pts.length === 1 ? `${d} l0.01 0` : d} {...commun} />
      )
  } else if (a.type === 'ligne' && pts.length > 1) {
    forme = <line x1={pts[0][0]} y1={pts[0][1]} x2={pts[1][0]} y2={pts[1][1]} {...commun} />
  } else if (a.type === 'fleche' && pts.length > 1) {
    const [p0, p1] = pts
    const ang = Math.atan2(p1[1] - p0[1], p1[0] - p0[0])
    const t = Math.max(w * 5, 14)
    const g: Point = [p1[0] - t * Math.cos(ang - 0.45), p1[1] - t * Math.sin(ang - 0.45)]
    const dr: Point = [p1[0] - t * Math.cos(ang + 0.45), p1[1] - t * Math.sin(ang + 0.45)]
    forme = (
      <>
        <line x1={p0[0]} y1={p0[1]} x2={p1[0] - (t * 0.6) * Math.cos(ang)} y2={p1[1] - (t * 0.6) * Math.sin(ang)} {...commun} />
        <polygon points={`${p1[0]},${p1[1]} ${g[0]},${g[1]} ${dr[0]},${dr[1]}`} fill={a.couleur} stroke={a.couleur} strokeWidth={w * 0.6} strokeLinejoin="round" />
      </>
    )
  } else if (a.type === 'cercle' && pts.length > 1) {
    const [p0, p1] = pts
    forme = <ellipse cx={(p0[0] + p1[0]) / 2} cy={(p0[1] + p1[1]) / 2} rx={Math.abs(p1[0] - p0[0]) / 2} ry={Math.abs(p1[1] - p0[1]) / 2} {...commun} />
  } else if (a.type === 'texte' && pts.length) {
    const fs = taillePolice(a.epaisseur)
    forme = (
      <text x={pts[0][0]} y={pts[0][1]} fontSize={fs} fontWeight={700} fill={a.couleur} stroke="#fff" strokeWidth={fs * 0.2} strokeLinejoin="round" style={{ paintOrder: 'stroke', fontFamily: 'Arial, sans-serif' }}>
        {(a.texte ?? '').split('\n').map((l, i) => (
          <tspan key={i} x={pts[0][0]} dy={i ? fs * 1.25 : 0}>
            {l || ' '}
          </tspan>
        ))}
      </text>
    )
  }

  if (!choisie) return <g>{forme}</g>
  // Contour de sélection.
  let x0: number, y0: number, x1: number, y1: number
  if (a.type === 'texte') {
    const b = boiteTexte(a as AnnotationLocale, ratio)
    ;[x0, y0, x1, y1] = [b.x0 * L, b.y0 * H, b.x1 * L, b.y1 * H]
  } else {
    const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1])
    ;[x0, y0, x1, y1] = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)]
  }
  const m = Math.max(w, 4) + 4
  return (
    <g>
      {forme}
      <rect x={x0 - m} y={y0 - m} width={x1 - x0 + 2 * m} height={y1 - y0 + 2 * m} fill="none" stroke="#4668B1" strokeWidth={1.5} strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />
    </g>
  )
}

export interface NouvelleForme {
  type: TypeAnnotation
  geometrie: { p: Point[] }
}

/**
 * La couche d'annotations, posée exactement sur la page.
 * Avec un outil de dessin, un doigt dessine ; deux doigts zooment et
 * déplacent le plan.
 */
export function CoucheAnnotations({
  annotations,
  ratio,
  outil,
  couleur,
  epaisseur,
  choisie,
  choisir,
  creer,
  demanderTexte
}: {
  annotations: AnnotationLocale[]
  ratio: number
  outil: Outil | null
  couleur: string
  epaisseur: number
  choisie: string | null
  choisir: (id: string | null) => void
  creer: (f: NouvelleForme) => void
  demanderTexte: (p: Point) => void
}) {
  const svg = useRef<SVGSVGElement>(null)
  const [brouillon, setBrouillon] = useState<NouvelleForme | null>(null)
  const enCours = useRef<NouvelleForme | null>(null)
  const doigts = useRef(new Set<number>())
  const depart = useRef<{ x: number; y: number } | null>(null)

  const dessin = outil !== null && outil !== 'main' && outil !== 'choisir'

  // Changement d'outil : on abandonne un tracé inachevé.
  useEffect(() => {
    enCours.current = null
    setBrouillon(null)
  }, [outil])

  function position(e: React.PointerEvent): Point {
    const r = svg.current!.getBoundingClientRect()
    return [
      Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))
    ]
  }

  function abandonner() {
    enCours.current = null
    setBrouillon(null)
  }

  return (
    <svg
      ref={svg}
      className="annot-couche"
      viewBox={`0 0 ${L} ${L * ratio}`}
      preserveAspectRatio="none"
      style={{
        pointerEvents: outil && outil !== 'main' ? 'auto' : 'none',
        touchAction: dessin ? 'none' : 'pan-x pan-y',
        cursor: dessin ? (outil === 'texte' ? 'text' : 'crosshair') : outil === 'choisir' ? 'pointer' : undefined
      }}
      onPointerDown={(e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return
        doigts.current.add(e.pointerId)
        if (doigts.current.size > 1) {
          // Deuxième doigt : c'est un pincement, pas un trait.
          abandonner()
          return
        }
        const p = position(e)
        if (outil === 'choisir') {
          const r = svg.current!.getBoundingClientRect()
          const seuil = 14 / r.width // 14 pixels d'écran autour du doigt
          let meilleure: AnnotationLocale | null = null
          let dMin = seuil
          for (const a of annotations) {
            const d = distance(p, a, ratio)
            if (d <= dMin) {
              dMin = d
              meilleure = a
            }
          }
          if (meilleure) e.stopPropagation()
          choisir(meilleure?.id ?? null)
          return
        }
        if (!dessin) return
        e.stopPropagation()
        e.preventDefault()
        svg.current?.setPointerCapture(e.pointerId)
        depart.current = { x: e.clientX, y: e.clientY }
        if (outil === 'texte') return
        const f: NouvelleForme = { type: outil as TypeAnnotation, geometrie: { p: [p, p] } }
        if (outil === 'libre' || outil === 'surligneur') f.geometrie.p = [p]
        enCours.current = f
        setBrouillon(f)
      }}
      onPointerMove={(e) => {
        const f = enCours.current
        if (!f || doigts.current.size > 1) return
        const p = position(e)
        let pts = f.geometrie.p
        if (f.type === 'libre' || f.type === 'surligneur') {
          const der = pts[pts.length - 1]
          if (Math.hypot(p[0] - der[0], (p[1] - der[1]) * ratio) < 0.0015) return
          pts = [...pts, p]
        } else {
          pts = [pts[0], p]
        }
        enCours.current = { ...f, geometrie: { p: pts } }
        setBrouillon(enCours.current)
      }}
      onPointerUp={(e) => {
        const seul = doigts.current.size === 1
        doigts.current.delete(e.pointerId)
        if (!seul) return
        if (outil === 'texte' && depart.current) {
          const bouge = Math.hypot(e.clientX - depart.current.x, e.clientY - depart.current.y)
          if (bouge < 12) demanderTexte(position(e))
        }
        depart.current = null
        const f = enCours.current
        abandonner()
        if (!f) return
        const pts = f.geometrie.p
        const xs = pts.map((q) => q[0]), ys = pts.map((q) => q[1])
        const taille = Math.max(Math.max(...xs) - Math.min(...xs), (Math.max(...ys) - Math.min(...ys)) * ratio)
        // Un simple toucher ne crée rien, sauf un point à main levée.
        if (taille < 0.004 && f.type !== 'libre') return
        creer(f)
      }}
      onPointerCancel={(e) => {
        doigts.current.delete(e.pointerId)
        depart.current = null
        abandonner()
      }}
    >
      {annotations.map((a) => (
        <Forme key={a.id} a={a} ratio={ratio} choisie={a.id === choisie} />
      ))}
      {brouillon && (
        <Forme
          a={{ ...brouillon, couleur, epaisseur, texte: null }}
          ratio={ratio}
        />
      )}
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/* Barre d'outils                                                      */
/* ------------------------------------------------------------------ */

const ico = {
  viewBox: '0 0 24 24',
  width: 22,
  height: 22,
  stroke: 'currentColor',
  fill: 'none',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true
}

const OUTILS: { outil: Outil; nom: string; icone: ReactNode }[] = [
  { outil: 'main', nom: 'Déplacer le plan', icone: <svg {...ico}><path d="M8 13V5.5a1.5 1.5 0 013 0V11m0-1V4.5a1.5 1.5 0 013 0V11m0-.5V6a1.5 1.5 0 013 0v7c0 4-2.5 7-6.5 7S5 17.5 4 15l-1.2-2.6a1.4 1.4 0 012.4-1.4L8 13" /></svg> },
  { outil: 'choisir', nom: 'Choisir une annotation', icone: <svg {...ico}><path d="M5 3l14 7-6 1.8L10.5 18z" /></svg> },
  { outil: 'texte', nom: 'Texte', icone: <svg {...ico}><path d="M5 6V4h14v2M12 4v16M9 20h6" /></svg> },
  { outil: 'surligneur', nom: 'Surligneur', icone: <svg {...ico}><path d="M15 4l5 5-8 8H7v-5z" /><path d="M4 21h16" strokeWidth={3.5} opacity={0.45} /></svg> },
  { outil: 'ligne', nom: 'Ligne', icone: <svg {...ico}><path d="M5 19L19 5" /></svg> },
  { outil: 'fleche', nom: 'Flèche', icone: <svg {...ico}><path d="M5 19L19 5M10 5h9v9" /></svg> },
  { outil: 'cercle', nom: 'Cercle', icone: <svg {...ico}><ellipse cx="12" cy="12" rx="8.5" ry="6.5" /></svg> },
  { outil: 'libre', nom: 'Main levée', icone: <svg {...ico}><path d="M3 15c3-6 5-8 7-6s-2 7 1 8 5-7 7-7 2 3 3 4" /></svg> }
]

export function BarreAnnotation({
  outil,
  setOutil,
  couleur,
  setCouleur,
  epaisseur,
  setEpaisseur,
  peutAnnuler,
  annuler
}: {
  outil: Outil | null
  setOutil: (o: Outil) => void
  couleur: string
  setCouleur: (c: string) => void
  epaisseur: number
  setEpaisseur: (e: number) => void
  peutAnnuler: boolean
  annuler: () => void
}) {
  return (
    <div className="annot-barre">
      <div className="annot-rang">
        {OUTILS.map((o) => (
          <button
            key={o.outil}
            className={`annot-outil${outil === o.outil ? ' actif' : ''}`}
            onClick={() => setOutil(o.outil)}
            title={o.nom}
            aria-label={o.nom}
            aria-pressed={outil === o.outil}
          >
            {o.icone}
          </button>
        ))}
      </div>
      <div className="annot-rang">
        {COULEURS.map((c) => (
          <button
            key={c.valeur}
            className={`annot-couleur${couleur === c.valeur ? ' actif' : ''}`}
            style={{ background: c.valeur }}
            onClick={() => setCouleur(c.valeur)}
            title={c.nom}
            aria-label={`Couleur ${c.nom}`}
            aria-pressed={couleur === c.valeur}
          />
        ))}
        <span className="annot-sep" />
        {EPAISSEURS.map((ep, i) => (
          <button
            key={ep.valeur}
            className={`annot-outil${epaisseur === ep.valeur ? ' actif' : ''}`}
            onClick={() => setEpaisseur(ep.valeur)}
            title={ep.nom}
            aria-label={`Épaisseur ${ep.nom}`}
            aria-pressed={epaisseur === ep.valeur}
          >
            <span style={{ display: 'block', width: 18, height: 2 + i * 3, background: 'currentColor', borderRadius: 3 }} />
          </button>
        ))}
        <span className="annot-sep" />
        <button className="annot-outil" onClick={annuler} disabled={!peutAnnuler} title="Annuler ma dernière annotation" aria-label="Annuler ma dernière annotation">
          <svg {...ico}><path d="M9 14L4 9l5-5" /><path d="M4 9h10a6 6 0 010 12h-3" /></svg>
        </button>
      </div>
    </div>
  )
}

/** Bandeau affiché quand une annotation est choisie : auteur, date, suppression. */
export function BandeauChoix({
  auteur,
  date,
  enAttente,
  peutSupprimer,
  supprimer,
  fermer,
  taille
}: {
  auteur: string
  date: string
  enAttente: boolean
  peutSupprimer: boolean
  supprimer: () => void
  fermer: () => void
  /** Pour un texte modifiable : réduire ou agrandir. */
  taille?: { reduire: () => void; agrandir: () => void; min: boolean; max: boolean }
}) {
  return (
    <div className="annot-choix">
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong>{auteur}</strong>
        <span className="code" style={{ marginLeft: 8 }}>
          {date}
          {enAttente ? ' · pas encore envoyée' : ''}
        </span>
      </div>
      {taille && (
        <>
          <button className="annot-outil" onClick={taille.reduire} disabled={taille.min} aria-label="Réduire le texte" title="Réduire le texte">
            <span style={{ fontWeight: 800, fontSize: 13 }}>A−</span>
          </button>
          <button className="annot-outil" onClick={taille.agrandir} disabled={taille.max} aria-label="Agrandir le texte" title="Agrandir le texte">
            <span style={{ fontWeight: 800, fontSize: 18 }}>A+</span>
          </button>
        </>
      )}
      {peutSupprimer && (
        <button className="bouton creux" style={{ padding: '7px 12px', minWidth: 0, width: 'auto' }} onClick={supprimer}>
          Supprimer
        </button>
      )}
      <button className="fermer-x" onClick={fermer} aria-label="Fermer">
        ✕
      </button>
    </div>
  )
}

/** Saisie du texte d'une annotation. */
export function DialogueTexte({ valider, annuler }: { valider: (t: string) => void; annuler: () => void }) {
  const [texte, setTexte] = useState('')
  const champ = useRef<HTMLTextAreaElement>(null)
  useEffect(() => champ.current?.focus(), [])
  const ok = () => (texte.trim() ? valider(texte.trim()) : annuler())
  return (
    <div className="annot-voile" onPointerDown={(e) => e.target === e.currentTarget && annuler()}>
      <div className="annot-dialogue">
        <strong>Texte de l'annotation</strong>
        <textarea
          ref={champ}
          rows={3}
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              ok()
            } else if (e.key === 'Escape') annuler()
          }}
          placeholder="Ex. : pare-vapeur à reprendre"
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="bouton creux" style={{ minWidth: 0, width: 'auto' }} onClick={annuler}>
            Annuler
          </button>
          <button className="bouton" style={{ minWidth: 0, width: 'auto' }} onClick={ok}>
            Ajouter
          </button>
        </div>
      </div>
    </div>
  )
}
